/** Encaissement — aligné sur `payments.php`. */

export type PaymentStatus = "completed" | "partial" | "refunded" | "pending" | "failed";

export type Payment = {
  id: number;
  number?: string | null;
  document_id: number;
  document_number?: string | null;
  client_id: number;
  client_name?: string | null;
  paid_at: string;
  amount_cents: number;
  refunded_cents: number;
  applied_cents: number;
  /** TTC de la facture, pour signaler un trop-perçu à l’édition. */
  invoice_ttc_cents?: number | null;
  /** Encaissé net déjà posé sur la facture, ce paiement compris. */
  document_paid_cents?: number | null;
  method: string;
  method_label?: string;
  reference?: string | null;
  note?: string | null;
  status: PaymentStatus;
  status_label?: string;
  /** Prélèvement GoCardless pas encore encaissé : pas d’actions de reçu. */
  track_only?: boolean;
  /** Jour où GoCardless débite le compte. */
  charge_on?: string | null;
};

export const PAY_KINDS: { value: string; label: string }[] = [
  { value: "transfer", label: "Virement bancaire" },
  { value: "check", label: "Chèque" },
  { value: "cash", label: "Espèces" },
  { value: "card", label: "Carte bancaire" },
  { value: "sepa", label: "Prélèvement SEPA" },
];

export function asPayment(row: Payment): Payment {
  const amount = Number(row.amount_cents) || 0;
  const refunded = Math.max(0, Number(row.refunded_cents) || 0);
  const applied = row.applied_cents !== undefined ? Number(row.applied_cents) || 0 : Math.max(0, amount - refunded);
  const status = (row.status as PaymentStatus) || (refunded >= amount && amount > 0 ? "refunded" : refunded > 0 ? "partial" : "completed");
  return {
    ...row,
    id: Number(row.id),
    document_id: Number(row.document_id),
    client_id: Number(row.client_id) || 0,
    amount_cents: amount,
    refunded_cents: refunded,
    applied_cents: applied,
    invoice_ttc_cents: row.invoice_ttc_cents != null ? Number(row.invoice_ttc_cents) || 0 : row.invoice_ttc_cents,
    document_paid_cents: row.document_paid_cents != null ? Number(row.document_paid_cents) || 0 : row.document_paid_cents,
    status,
  };
}

export function payMethodLabel(row: Payment): string {
  return row.method_label || PAY_KINDS.find((k) => k.value === row.method)?.label || row.method || "—";
}

export function payStatusLabel(row: Payment): string {
  if (row.status_label) return row.status_label;
  if (row.status === "refunded") return "Remboursé";
  if (row.status === "partial") return "Remboursé partiel";
  return "Encaissé";
}

export function payStatusClass(status: PaymentStatus): string {
  if (status === "pending") return "is-collecting";
  if (status === "failed") return "is-rejected";
  if (status === "refunded") return "is-credited";
  if (status === "partial") return "is-viewed";
  return "is-sent";
}

/** Reste de la facture. Négatif = trop-perçu. */
export function invoiceRemainingCents(row: { status?: string; credited?: boolean; total_ttc_cents?: number; remaining_cents?: number; paid_cents?: number }): number | null {
  if ((row.status ?? "") === "draft") return null;
  if (row.credited) return 0;
  const ttc = Number(row.total_ttc_cents) || 0;
  const paid = Number(row.paid_cents) || 0;
  if (row.paid_cents !== undefined && row.paid_cents !== null) return ttc - paid;
  if (row.remaining_cents !== undefined && row.remaining_cents !== null) return Number(row.remaining_cents) || 0;
  return ttc;
}

export function invoiceSoldeText(cents: number | null, money: (n: number) => string): string {
  if (cents == null) return "—";
  if (cents === 0) return "Soldé";
  return money(cents);
}

export function invoiceSoldeTone(cents: number | null): string {
  if (cents == null || cents === 0) return "";
  return cents > 0 ? "is-due fx-bal-due" : "is-credit fx-bal-credit";
}
